/**
 * THE read-only gate — round 7 task 10, controller ruling 12.
 *
 * One function, so `SurveyProvider`'s `write()` (every document write:
 * `setAnswer`, `clearAnswer`, `setStructure`, `applyDefaults`, `applyFix`,
 * `addNote`) and `usePhotoCapture`'s `capture()` / `remove()` (the two
 * places `store.addPhoto` / `store.deletePhoto` are reached, which bypass
 * the provider entirely) enforce exactly the same refusal, logged the same
 * way — instead of two copies of the same `if (readOnly) { … }` free to
 * drift apart.
 *
 * Pure: no React, no store. `run` is never called on a refusal, which is
 * what proves a submitted survey cannot be written to no matter which door
 * was tapped — this is the one place that claim is checkable without
 * mounting a component (fix round 1, finding 1).
 */

import type { LogSink } from '../../core/log';

export interface WriteGateContext {
  /** Whether the write must be refused. */
  readOnly: boolean;
  surveyId: string;
  /** Which door this is — `survey.write`, `photo.capture`, `photo.remove`. */
  door: string;
  log: LogSink;
}

/**
 * Runs `run()` unless `ctx.readOnly`, in which case `run` is never invoked, a
 * `survey.write_refused` line goes to `ctx.log`, and `null` comes back.
 *
 * Never throws on a refusal — a refusal is not a failure a screen should
 * surface, it is a door that was never open (the caller already disabled or
 * hid the control that would have reached this).
 */
export async function guardedWrite<T>(ctx: WriteGateContext, run: () => Promise<T>): Promise<T | null> {
  if (ctx.readOnly) {
    ctx.log({
      level: 'warn',
      event: 'survey.write_refused',
      reason: 'submitted',
      surveyId: ctx.surveyId,
      door: ctx.door,
    });
    return null;
  }
  return run();
}
